import { useDisplay } from "vuetify/lib/framework.mjs";
export const useHelper = () => {
  /**
   * Secure the string to avoid injecting HTML and replace carriage return by a <BR> tag.
   * @param str The string to secure
   * @returns The secured string.
   */
  const secureStringAndReplaceNewLineToBr = function (str: string) {
    return str
      ? str
          .replace("<", "&lt;")
          .replace(">", "&gt;")
          .replace(/(?:\r\n|\r|\n)/g, "<br />")
      : "";
  };

  /**
   * Get the default Vuetify field density according to screen width
   * @returns
   */
  const defaultFieldDensity = function ():
    | null
    | "default"
    | "comfortable"
    | "compact" {
    // input field are smaller on mobile

    return useDisplay()?.smAndDown?.value ? "compact" : "default";
  };

  /**
   * Get the default Vuetify button size according to screen width
   * @returns
   */
  const defaultButtonSize = function (): string | number {
    // buttons are bigger on mobile
    return useDisplay()?.smAndDown?.value ? "large" : "default";
  };

  /**
   * Convert a Date to the fr-CH format
   * @param date - The date to convert
   * @returns The formatted date or an empty string if the Date is not valid
   * @example 15.01.2023
   */
  const convertDateToString = function (
    date: Date,
    displayTime: boolean = false
  ): string {
    if (!checkDateIsValid(date)) return "";

    const format = displayTime ? "DD.MM.YYYY HH:mm" : "DD.MM.YYYY";

    return useDateFormat(date, format).value;
  };

  /**
   * Convert a Date to a technical string format for Date type inputs
   * @param date - The date to convert
   * @returns The formatted date or an empty string if the Date is not valid
   * @example 2023-01-15
   */
  const convertDateToTechnicalString = function (date: Date): string {
    if (!checkDateIsValid(date)) return "";

    return useDateFormat(date, "YYYY-MM-DD").value;
  };

  /**
   * Check if the object in parameter is a valid Javascript Date
   * @param date
   * @returns
   */
  const checkDateIsValid = function (date: Date) {
    if (Object.prototype.toString.call(date) === "[object Date]") {
      if (isNaN(date.getTime())) {
        return false;
      } else {
        return true;
      }
    } else {
      return false;
    }
  };

  const checkDateStringIsValid = function (
    date: string,
    minValue = new Date("1900-01-01"),
    maxValue = new Date("2999-12-31")
  ) {
    const d = new Date(date);
    if (!checkDateIsValid(d)) return false;

    if (minValue <= d && d <= maxValue) return true;

    return false;
  };

  /**
   * Download a file from the browser and open or download it.
   * @param file The file in Blob object
   * @param filename The name of the file to save
   * @param openFileInBrowser true if open the file in a new tab rather than downloading it
   */
  const downloadFile = function (
    file: Blob,
    filename: string,
    openFileInBrowser: boolean
  ) {
    // IE10+
    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(new Blob([data], { type: fileType }), fileName);
      return;
    }

    var isIos =
      window.navigator && // /Macintosh/.test(navigator.userAgent) ||
      (/iPhone/.test(navigator.userAgent) || /iPad/.test(navigator.userAgent));

    var isChromeIOS = /CriOS\/[\d]+/.test(navigator.userAgent);

    const link = document.createElement("a");
    document.body.appendChild(link);
    const URL = window.URL || window.webkitURL;
    link.href = URL.createObjectURL(file);
    link.setAttribute("target", "_blank");
    // Download file rather than opening it in the browser
    // Force download on Safari mobile because opening in new tab doesn't work
    if (!openFileInBrowser || (isIos && !isChromeIOS)) {
      link.download = filename;
    }
    link.click();
    setTimeout(() => {
      URL.revokeObjectURL(link.href);
      document.body.removeChild(link);
    }, 1000);
  };

  /**
   * Vuetify validation rule to allow only valid chars.
   * @param input The input string to validate
   * @param errorMessage The error message to display if invalid
   * @returns
   */
  const ruleValidationOnlyChars = function (
    input: string,
    errorMessage: string
  ): string | boolean {
    if (!input) return true;
    const result = input.match(
      /[0-9A-Za-z-.,/\\^'¨~& \u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff]+/g
    );
    return (result && result.length > 0 && result[0] === input) || errorMessage;
  };

  const ruleValidationEmail = function (input: string): boolean {
    return /^(([^<>()[\]\\.,;:\s@']+(\.[^<>()\\[\]\\.,;:\s@']+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
      input
    );
  };

  const ruleValidationPhoneNumber = function (input: string): boolean {
    return /[+][0-9]{8,20}$/g.test(input);
  };

  const isValidGUID = function (value?: string): boolean {
    if (!value) return false;

    return /^(\{){0,1}[0-9a-fA-F]{8}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{12}(\}){0,1}$/.test(
      value
    );
  };

  return {
    secureStringAndReplaceNewLineToBr,
    defaultFieldDensity,
    defaultButtonSize,
    convertDateToString,
    convertDateToTechnicalString,
    checkDateIsValid,
    checkDateStringIsValid,
    downloadFile,
    ruleValidationOnlyChars,
    ruleValidationEmail,
    ruleValidationPhoneNumber,
    isValidGUID,
  };
};
